// LE PARCOURS D'UNE ÉTUDE : Capter → Découper → Atelier → Conclure.
//
// Plus de bloc à part : chaque étape EST un onglet de la fiche d'étude (une
// seule ligne d'onglets). `useStudyPath` dit où en est chaque étape (lu dans le
// terrain : entretiens, verbatims, post-it, insights) ; `StepHeader` l'affiche
// en tête de son onglet, avec le bouton qui mène plus loin.
//
// Il ne stocke rien. « Traiter dans l'atelier » dépose d'abord tout le terrain
// de l'étude qui n'y est pas encore, puis ouvre l'atelier filtré sur elle.
import { openTab } from '../components/Tabs';
import { useState } from 'react';
import { useBoard, useDepositStudy, useInsights, useInterviews, useStudySources, useStudyVerbatims } from '../api/queries';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner } from '../components/ui';
import { useRouter } from '../router';
import { sourcePath } from '../sources/StudySources';
import './studies.css';

export type StepKey = 'capter' | 'decouper' | 'atelier' | 'conclure';
type StepState = 'done' | 'doing' | 'todo';
const STATE_LABEL: Record<StepState, string> = { done: 'Fait', doing: 'En cours', todo: 'À faire' };

export interface Step {
  key: StepKey;
  hint: string;
  detail: string;
  state: StepState;
  /** Le nombre qu'affiche l'onglet (entretiens, verbatims, groupes, insights). */
  count: number;
  /** Ce qui mène AILLEURS que l'onglet lui-même (le reste est dans l'onglet). */
  action: { label: string; run: () => void; disabled?: boolean; loading?: boolean } | null;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n > 1 ? many : one}`;

export function useStudyPath({ projectId, studyId, canWrite, canInvite = false }: {
  projectId: string; studyId: string; canWrite: boolean;
  /** L'étude a des séances (entretien, observation, test) : on commence par contacter les participants. */
  canInvite?: boolean;
}) {
  const interviews = useInterviews(projectId, studyId);
  const verbatims = useStudyVerbatims(studyId);
  const sources = useStudySources(studyId);
  const board = useBoard(projectId);
  const insights = useInsights(projectId);
  const deposit = useDepositStudy(projectId);
  const { navigate } = useRouter();
  const [error, setError] = useState<string | null>(null);

  const nInterviews = interviews.data?.interviews.length ?? 0;
  const nVerbatims = verbatims.data?.length ?? 0;
  // Les transcriptions qu'on peut découper (texte gardé), et celles où rien
  // n'a encore été retenu : c'est là qu'il reste à faire.
  const readable = (sources.data ?? []).filter((s) => s.readable);
  const untouched = readable.filter((s) => s.verbatims === 0);
  // Le terrain de l'étude sur le plan : ses post-it de verbatim, et ceux rangés.
  const studyVerbatims = new Set((verbatims.data ?? []).map((v) => v.id));
  const studyNotes = (board.data?.notes ?? []).filter((n) => n.verbatimId && studyVerbatims.has(n.verbatimId));
  const grouped = studyNotes.filter((n) => n.groupId);
  const nGroups = new Set(grouped.map((n) => n.groupId)).size;
  const studyInsights = (insights.data ?? []).filter((i) => i.studies.some((s) => s.id === studyId));
  const toComplete = studyInsights.filter((i) => i.quality.level !== 'ok').length;

  const atelierPath = `/projet/${encodeURIComponent(projectId)}/atelier/${encodeURIComponent(studyId)}`;
  async function openAtelier() {
    setError(null);
    try {
      // Rien à déposer (ou lecture seule) : on ouvre simplement l'atelier.
      if (canWrite && studyNotes.length < nVerbatims) await deposit.mutateAsync(studyId);
      navigate(atelierPath);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Le dépôt dans l’atelier a échoué.');
    }
  }

  const captured = nInterviews > 0 || nVerbatims > 0 || readable.length > 0;
  // « Découper » mène à la transcription à traiter : la première pas encore
  // découpée, sinon la plus récente.
  const toCut = untouched[0] ?? readable[0] ?? null;
  const steps: Step[] = [
    {
      key: 'capter',
      hint: 'Enregistrer un entretien, importer une transcription, des notes, des photos.',
      detail: nInterviews > 0 ? plural(nInterviews, 'entretien') : captured ? 'Terrain importé' : 'Rien de capté pour l’instant',
      state: captured ? 'done' : 'todo',
      count: nInterviews,
      // Rien de capté encore : le premier geste est de contacter les participants.
      action: !captured && canInvite ? { label: 'Entrer en contact', run: () => openTab('etude', 'participants') } : null,
    },
    {
      key: 'decouper',
      hint: 'Relire la transcription et surligner les passages qui serviront de preuves : les verbatims.',
      detail: [
        nVerbatims > 0 ? `${plural(nVerbatims, 'verbatim')} retenu${nVerbatims > 1 ? 's' : ''}` : 'Aucun verbatim',
        untouched.length > 0 ? `${plural(untouched.length, 'transcription')} à découper` : null,
      ].filter(Boolean).join(' · '),
      state: untouched.length > 0 ? (nVerbatims > 0 ? 'doing' : 'todo') : nVerbatims > 0 ? 'done' : 'todo',
      count: nVerbatims,
      action: toCut
        ? { label: untouched.length > 0 ? 'Découper la transcription' : 'Relire et découper', run: () => navigate(sourcePath(projectId, studyId, toCut.id)) }
        : null,
    },
    {
      key: 'atelier',
      hint: 'Ranger les verbatims en post-it, les regrouper à la main.',
      detail: nVerbatims === 0
        ? 'Rien à ranger encore : découpez d’abord des verbatims.'
        : `${studyNotes.length}/${nVerbatims} sur le plan · ${plural(nGroups, 'groupe')}`,
      state: nVerbatims > 0 && grouped.length === nVerbatims ? 'done' : studyNotes.length > 0 ? 'doing' : 'todo',
      count: nGroups,
      action: {
        label: canWrite ? 'Traiter dans l’atelier' : 'Voir l’atelier',
        run: () => void openAtelier(),
        disabled: nVerbatims === 0,
        loading: deposit.isPending,
      },
    },
    {
      key: 'conclure',
      hint: 'Tirer des insights, les qualifier et les prioriser.',
      detail: studyInsights.length > 0
        ? `${plural(studyInsights.length, 'insight')}${toComplete > 0 ? ` · ${toComplete} à compléter` : ''}`
        : 'Aucun insight',
      state: studyInsights.length === 0 ? 'todo' : toComplete > 0 ? 'doing' : 'done',
      count: studyInsights.length,
      action: null,
    },
  ];
  const byKey = Object.fromEntries(steps.map((s) => [s.key, s])) as Record<StepKey, Step>;
  return { byKey, error };
}

/** L'étape, en tête de son onglet : quoi faire, où on en est, et le bouton utile. */
export function StepHeader({ step, error = null }: { step: Step; error?: string | null }) {
  return (
    <div className={`step-head step-head-${step.state}`}>
      <div className="step-head-text">
        <span className="step-head-state">{step.state === 'done' ? '✓ ' : ''}{STATE_LABEL[step.state]}</span>
        <p className="step-head-hint">{step.hint}</p>
        <p className="step-head-detail">{step.detail}</p>
      </div>
      {step.action ? (
        <Button
          variant={step.state === 'done' ? 'ghost' : 'primary'}
          onClick={step.action.run}
          disabled={step.action.disabled}
          loading={step.action.loading}
        >
          {step.action.label}
        </Button>
      ) : null}
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
    </div>
  );
}
